import { Moon, Sun, UserCircle, UserSearch, Users, UsersRound, Waypoints } from 'lucide-react';
import { createElement, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useT } from '@/core/providers/I18nProvider';
import { useTheme } from '@/core/providers/ThemeProvider';

import type { PaletteAction } from './CommandPalette';

/**
 * The global (non-navigation) actions the ⌘K palette offers. Self-sources its
 * deps (theme, router, i18n) so the AppShell stays a thin consumer + under the
 * file-size ceiling. Nav destinations come from the sidebar items separately;
 * these are the "do it now" commands. Sign-out intentionally stays in the
 * UserMenu (its logout wiring lives there) — the palette focuses on jump-to +
 * the cheap, safe toggles.
 */
export function useCommandActions(): PaletteAction[] {
  const { t } = useT();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  return useMemo<PaletteAction[]>(() => {
    const group = t('palette.group.actions');
    const nav = t('palette.group.navigation');
    return [
      // Users-surface jump-to + actions (UGL-105 A0). The sidebar already
      // provides "Users & audiences" → /users; these reach the sub-routes +
      // the create/find actions the sidebar can't express.
      {
        id: 'nav-audiences',
        label: t('palette.nav.audiences'),
        group: nav,
        icon: createElement(UsersRound, { className: 'h-4 w-4' }),
        run: () => {
          void navigate('/users/audiences');
        },
      },
      // KEPT after WF-5.3 gave `/site-walk` a sidebar row, and the reason
      // changed rather than expiring. This entry existed because the page was
      // unreachable by name; it is now reachable, and the palette is still where
      // somebody who thinks "map my site" as a VERB looks — the sidebar answers
      // "where is that page", which is a different question. Every other row in
      // this nav group has a sidebar entry too.
      //
      // (The comment said "it has no sidebar row" until D6; a rationale that
      // outlives its premise is how a reader concludes the wrong thing about
      // why something is here.)
      {
        id: 'nav-site-walk',
        label: t('palette.nav.mapSite'),
        group: nav,
        icon: createElement(Waypoints, { className: 'h-4 w-4' }),
        run: () => {
          void navigate('/site-walk');
        },
      },
      {
        id: 'nav-user-insights',
        label: t('palette.nav.userInsights'),
        group: nav,
        icon: createElement(Users, { className: 'h-4 w-4' }),
        run: () => {
          void navigate('/users/insights');
        },
      },
      {
        id: 'create-audience',
        label: t('palette.action.createAudience'),
        group,
        icon: createElement(UsersRound, { className: 'h-4 w-4' }),
        run: () => {
          void navigate('/users/audiences?new=1');
        },
      },
      {
        id: 'find-user',
        label: t('palette.action.findUser'),
        group,
        icon: createElement(UserSearch, { className: 'h-4 w-4' }),
        run: () => {
          void navigate('/users');
        },
      },
      {
        id: 'toggle-theme',
        label: theme === 'dark' ? t('palette.action.lightMode') : t('palette.action.darkMode'),
        group,
        icon: createElement(theme === 'dark' ? Sun : Moon, { className: 'h-4 w-4' }),
        run: toggleTheme,
      },
      {
        id: 'account',
        label: t('palette.action.account'),
        group,
        icon: createElement(UserCircle, { className: 'h-4 w-4' }),
        run: () => {
          void navigate('/account');
        },
      },
    ];
  }, [t, theme, toggleTheme, navigate]);
}
